import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { patientApi, patientErr } from "@/lib/patientApi";
import { toast } from "sonner";
import { CaretLeft, Package, Truck, CreditCard, UploadSimple, CheckCircle } from "@phosphor-icons/react";
import { STATUS_BADGE } from "@/pages/ShopOrders";
import { loadSnap } from "@/components/BuyRelaxButton";

const rp = (n) => (n === null || n === undefined ? "—" : "Rp " + (Number(n) || 0).toLocaleString("id-ID"));
const PAY_BADGE = {
  menunggu_pembayaran: { t: "Menunggu Pembayaran", c: "bg-blue-100 text-blue-700" },
  menunggu_verifikasi: { t: "Menunggu Verifikasi", c: "bg-amber-100 text-amber-700" },
  paid: { t: "Lunas", c: "bg-emerald-100 text-emerald-700" },
  bukti_ditolak: { t: "Bukti Ditolak", c: "bg-rose-100 text-rose-600" },
  perlu_tinjauan: { t: "Perlu Tinjauan", c: "bg-orange-100 text-orange-700" },
};

export default function ShopOrderDetail() {
  const { orderNo } = useParams();
  const nav = useNavigate();
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => patientApi.get(`/patient/shop/orders/${orderNo}`).then((r) => setO(r.data)).catch((e) => { toast.error(patientErr(e)); nav("/pesanan-belanja"); });
  useEffect(() => {
    document.title = `${orderNo} — HalloGBS`;
    if (!localStorage.getItem("patient_token")) { nav(`/pasien/masuk?returnTo=/pesanan-belanja/${orderNo}`); return; }
    load();
  }, [orderNo, nav]);

  const payMidtrans = async () => {
    if (busy) return;  // cegah order/payment ganda saat klik berulang
    setBusy(true);
    try {
      const { data } = await patientApi.post(`/patient/shop/orders/${orderNo}/pay/midtrans`);
      if (String(data.snap_token).startsWith("STUB-")) {
        toast.info("Mode uji: token pembayaran dibuat. Status akan diperbarui setelah verifikasi server.");
      } else if (data.snap_token) {
        await loadSnap(data.client_key, data.is_production);  // MUAT skrip Snap dulu (root cause tombol diam)
        if (!window.snap) { toast.error("Gagal memuat Midtrans. Silakan coba lagi."); return; }
        window.snap.pay(data.snap_token, {
          onSuccess: () => { toast.success("Pembayaran diterima. Status diperbarui setelah verifikasi server."); load(); },
          onPending: () => { toast.info("Menunggu pembayaran Anda diselesaikan."); load(); },
          onError: () => toast.error("Pembayaran gagal. Silakan coba lagi."),
          onClose: () => {},
        });
      } else { toast.error("Gagal membuat pembayaran. Silakan coba lagi."); }
    } catch (e) { toast.error(patientErr(e)); } finally { setBusy(false); load(); }
  };
  const payManual = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/png", "application/pdf"].includes(f.type)) return toast.error("Bukti harus JPG/PNG/PDF");
    if (f.size > 5 * 1024 * 1024) return toast.error("Maksimal 5 MB");
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { await patientApi.post(`/patient/shop/orders/${orderNo}/pay/manual`, fd); toast.success("Bukti terkirim. Menunggu verifikasi admin."); }
    catch (e2) { toast.error(patientErr(e2)); } finally { setBusy(false); load(); }
  };

  if (!o) return <div className="flex min-h-screen items-center justify-center bg-[#FAF7FF] text-slate-400">Memuat…</div>;
  const b = STATUS_BADGE[o.status] || { t: o.status, c: "bg-slate-100 text-slate-600" };
  const pb = PAY_BADGE[o.payment_status];

  return (
    <div className="min-h-screen bg-[#FAF7FF]" data-testid="shop-order-detail-page">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-pink-100 bg-white/90 px-4 py-3 backdrop-blur">
        <Link to="/pesanan-belanja" data-testid="detail-back" className="flex items-center gap-1.5 text-sm font-bold text-[#7C4DE0]"><CaretLeft size={18} weight="bold" /> Pesanan</Link>
        <h1 className="font-heading text-base font-extrabold text-[#4C1D95]">{o.order_no}</h1>
        <span className="w-16" />
      </header>

      <div className="mx-auto max-w-2xl space-y-4 px-4 py-5">
        <div className="flex items-center justify-between rounded-2xl border border-pink-100 bg-white p-4">
          <span className="flex items-center gap-1.5 text-sm font-bold text-slate-600">{o.fulfillment_type === "pickup" ? <><Package size={18} weight="duotone" /> Ambil di GBS</> : <><Truck size={18} weight="duotone" /> Dikirim</>}</span>
          <span data-testid="detail-status" className={`rounded-full px-3 py-1 text-xs font-bold ${b.c}`}>{b.t}</span>
        </div>

        {o.status === "dibatalkan" && o.cancel_reason && (
          <div data-testid="detail-cancel-reason" className="rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700"><b>Dibatalkan:</b> {o.cancel_reason}</div>
        )}

        <section className="rounded-2xl border border-pink-100 bg-white p-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Item</p>
          <div className="space-y-2">
            {o.items.map((it, i) => (
              <div key={i} data-testid={`detail-item-${i}`} className="flex justify-between gap-2 text-sm">
                <span className="min-w-0 flex-1"><span className="font-semibold text-slate-800">{it.product_name}</span><span className="text-slate-400"> · {it.variant_label} × {it.qty}</span></span>
                <span className="shrink-0 font-bold text-slate-700">{rp(it.line_total)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-1 border-t border-pink-50 pt-3 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-bold">{rp(o.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Ongkir</span><span className="font-bold">{o.shipping_fee === null ? "Menunggu ongkir" : rp(o.shipping_fee)}</span></div>
            <div className="flex justify-between text-base"><span className="font-bold text-[#1E1B4B]">Total</span><span data-testid="detail-total" className="font-extrabold text-[#DB2777]">{o.total === null ? "Menunggu ongkir" : rp(o.total)}</span></div>
          </div>
        </section>

        <section className="rounded-2xl border border-pink-100 bg-white p-4 text-sm">
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Penerima</p>
          <p className="font-semibold text-slate-800">{o.recipient?.name}</p>
          <p className="text-slate-500">{o.recipient?.wa}</p>
          {o.address && <p className="mt-2 text-slate-600">{o.address.alamat}{o.address.catatan ? ` (${o.address.catatan})` : ""}</p>}
        </section>

        {o.status === "menunggu_ongkir" && (
          <div data-testid="detail-await-shipping" className="rounded-2xl border border-amber-100 bg-amber-50 p-4 text-center text-sm text-amber-700">Menunggu admin menetapkan ongkir. Pembayaran belum tersedia.</div>
        )}
        {o.status === "kedaluwarsa" && (
          <div data-testid="detail-expired" className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-600">Pesanan telah kedaluwarsa karena melewati batas waktu pembayaran. Stok telah dilepas kembali. Silakan pesan ulang bila masih diperlukan.</div>
        )}
        {o.status === "diproses" && (
          <div data-testid="detail-paid" className="flex items-center gap-2 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-700"><CheckCircle size={20} weight="fill" /> Pembayaran lunas — pesanan sedang diproses. Terima kasih!</div>
        )}
        {["siap_diambil", "dikirim", "selesai"].includes(o.status) && (
          <div data-testid="detail-fulfillment" className="rounded-2xl border border-indigo-100 bg-indigo-50/50 p-4 text-sm text-indigo-700">
            {o.status === "siap_diambil" && "Pesanan Anda siap diambil di GBS."}
            {o.status === "dikirim" && <>Pesanan dikirim{o.courier ? ` via ${o.courier}` : ""}{o.tracking_no ? ` · Resi: ${o.tracking_no}` : ""}.</>}
            {o.status === "selesai" && "Pesanan selesai. Terima kasih telah berbelanja di GBS!"}
          </div>
        )}
        {o.fulfillment_type === "delivery" && o.shipping && o.shipping.provider === "biteship" && o.shipping.biteship_order_id && (
          <div data-testid="detail-shipping" className="rounded-2xl border border-cyan-100 bg-cyan-50/40 p-4 text-sm">
            <p className="mb-1 font-bold text-cyan-800">Pengiriman</p>
            <p className="text-slate-600">Ekspedisi: <b>{o.shipping.courier_name} · {o.shipping.courier_service_name}</b></p>
            {o.shipping.eta && <p className="text-slate-600">Estimasi: {o.shipping.eta}</p>}
            <p className="text-slate-600 break-words">No. Resi: <b data-testid="detail-awb" className="break-all">{o.shipping.waybill_id || "menunggu diterbitkan kurir"}</b></p>
            {o.shipping.needs_review && <p className="mt-1 rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-700">Pengiriman sedang ditinjau tim GBS. Mohon tunggu.</p>}
            {o.shipping.tracking_link && <a data-testid="detail-track-link" href={o.shipping.tracking_link} target="_blank" rel="noreferrer" className="mt-2 inline-block rounded-xl bg-[#7C4DE0] px-4 py-2 text-xs font-bold text-white">Lacak Paket</a>}
            {o.shipping.history && o.shipping.history.length > 0 && (
              <div data-testid="detail-track-timeline" className="mt-3 space-y-1 border-t border-cyan-100 pt-2">
                {o.shipping.history.slice(-6).reverse().map((h, i) => (
                  <p key={i} className="text-xs text-slate-500"><b className="text-cyan-700">{h.state}</b> · {h.status}{h.note ? ` — ${h.note}` : ""}</p>
                ))}
              </div>
            )}
          </div>
        )}
        {o.status === "menunggu_pembayaran" && (
          <div className="space-y-3">
            {pb && <div className="text-center"><span data-testid="detail-payment-status" className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${pb.c}`}>{pb.t}</span></div>}
            {o.reject_reason && <p data-testid="detail-reject-reason" className="rounded-xl bg-rose-50 px-3 py-2 text-center text-xs text-rose-600">Bukti sebelumnya ditolak: {o.reject_reason}. Silakan bayar ulang.</p>}
            {o.payment_deadline && (
              <p data-testid="detail-deadline" className="text-center text-xs text-slate-500">Selesaikan pembayaran sebelum <b>{new Date(o.payment_deadline).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</b> agar pesanan tidak kedaluwarsa.</p>
            )}
            {o.payment_status === "menunggu_verifikasi" ? (
              <div data-testid="detail-await-verify" className="rounded-2xl border border-amber-100 bg-amber-50 p-4 text-center text-sm text-amber-700">Bukti transfer Anda sedang diverifikasi admin.</div>
            ) : (
              <>
                <button data-testid="detail-pay-midtrans" disabled={busy} onClick={payMidtrans} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#7C4DE0] py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#6A38C7] active:scale-[0.99] disabled:opacity-50"><CreditCard size={18} weight="fill" /> Bayar Online (Midtrans)</button>
                <label data-testid="detail-pay-manual" className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl border-2 border-[#7C4DE0] py-3 text-sm font-bold text-[#6A38C7]"><UploadSimple size={18} weight="bold" /> Transfer Manual (unggah bukti)<input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={payManual} disabled={busy} /></label>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
